import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  ButtonGroup,
  Grid,
} from "@mui/material";
import { Upload } from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import { Cancel } from "@mui/icons-material";

export default function UploadFileModal({
  optionVariantID,
  addToCustomFiles,
  isModalOpen = false,
  onModalClose,
  isNight = false,
}) {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    setOpen(isModalOpen);
  }, [isModalOpen]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleUpload = () => {
    if (!selectedFile) {
      alert("Please select a valid file first.");
      return;
    }

    // Convert file to base64
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result; // full base64 string with prefix

      // Close modal
      setOpen(false);
      onModalClose(previewUrl);

      // Pass base64 to parent function
      addToCustomFiles(optionVariantID, base64String);
    };

    reader.onerror = () => {
      toast.error("Error reading file. Please try again.");
    };

    // Start conversion
    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const maxSize = 500 * 1024;

    if (file) {
      if (file.size > maxSize) {
        toast.error("File size exceeds 500 KB. Please choose a smaller file.", {
          position: "bottom-center",
          autoClose: 5000,
          hideProgressBar: true,
          theme: "light",
        });
        setSelectedFile(null);
        setPreviewUrl(null);
      } else {
        setSelectedFile(file);

        // ✅ If it's an image, create a preview
        if (file.type.startsWith("image/")) {
          const imageUrl = URL.createObjectURL(file);
          setPreviewUrl(imageUrl);
        } else {
          setPreviewUrl(null);
        }
      }
    }

    // Reset file input value so user can re-upload same file
    event.target.value = "";
  };

  return (
    <Dialog
      key={optionVariantID}
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          backgroundColor: isNight ? "#3d4658" : "#fff",
          color: isNight ? "#eef2f7" : "inherit",
          transition: "color 0.35s ease, background-color 0.35s ease",
        },
      }}
    >
      <Grid display={"flex"} sx={{}}>
        <Grid
          sx={{
            marginTop: "33px",
            marginLeft: "3vh",
          }}
        >
          <Box
            sx={{
              height: "75px",
              width: "75px",
              cursor: "pointer",
              backgroundColor: previewUrl
                ? ""
                : isNight
                  ? "#566176"
                  : "gainsboro",
              backgroundImage: previewUrl ? "url(" + previewUrl + ")" : "",
              backgroundSize: "cover",
              position: "relative",
              transition: "background-color 0.35s ease",
            }}
          >
            <Box sx={{ height: "20px", pointerEvents: "none" }}></Box>
            <p
              style={{
                fontSize: "calc(0.685rem + var(--large-screen-font-bump))",
                textAlign: "center",
                pointerEvents: "none",
                color: isNight ? "#eef2f7" : "inherit",
                transition: "color 0.35s ease",
              }}
            >
              {previewUrl ? "" : "Choose File"}
            </p>
            <input
              type="file"
              onChange={handleFileChange}
              className="border p-2 w-full"
              style={{
                opacity: 0,
                height: "75px",
                width: "75px",
                cursor: "pointer",
                position: "absolute",
                top: 0,
              }}
            />
            {selectedFile && (
              <Box
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
              >
                <Cancel
                  style={{
                    width: "17px",
                    position: "absolute",
                    right: "0",
                    top: "0",
                    color: isNight ? "#eef2f7" : "#d32f2f",
                    cursor: "pointer",
                    transition: "color 0.35s ease",
                  }}
                ></Cancel>
              </Box>
            )}
          </Box>
          {/* {error && <p style={{ fontSize: "9px" }}>{error}</p>} */}
          <ToastContainer position="bottom-center" hideProgressBar={true} />
          <Box
            sx={{
              width: "75px",
              overflow: "hidden",
              textAlign: "center",
            }}
          >
            {selectedFile && (
              <p
                style={{
                  fontSize: "calc(0.685rem + var(--large-screen-font-bump))",
                  color: isNight ? "#eef2f7" : "inherit",
                  transition: "color 0.35s ease",
                }}
              >
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </Box>
        </Grid>
        {/* FileUpload */}
        <Grid sx={{}}>
          <DialogTitle
            sx={{
              fontSize: "calc(0.91rem + var(--large-screen-font-bump))",
              fontWeight: "bold",
              padding: "3vh 3vh 2vh 3vh",
            }}
          >
            Upload your custom design here
          </DialogTitle>
          <DialogContent sx={{ padding: "3vh 3vh" }}>
            <Typography
              sx={{
                fontSize: "calc(0.86rem + var(--large-screen-font-bump))",
              }}
            >
              Upload a file here that describe the custom design you would like
              to have
            </Typography>
            <Box height={"3vh"}></Box>
            <ButtonGroup sx={{ float: "right" }}>
              <Button
                onClick={() => {
                  setOpen(false);
                  if (onModalClose) onModalClose(setPreviewUrl);
                }}
                variant="outlined"
                color="primary"
                sx={{
                  fontSize: "calc(0.72rem + var(--large-screen-font-bump))",
                  borderRadius: "0",
                  backgroundColor: "#transparent",
                  color: isNight ? "#eef2f7" : "#d32f2f",
                  fontWeight: "bold",
                  border: "none !important;",
                  marginRight: "2vh",
                  transition: "color 0.35s ease",
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={selectedFile === null}
                variant="contained"
                onClick={() => {
                  // upload
                  handleUpload();
                }}
                sx={{
                  fontSize: "calc(0.72rem + var(--large-screen-font-bump))",
                  borderRadius: "0",
                  backgroundColor: selectedFile
                    ? "#d32f2f"
                    : isNight
                      ? "#566176"
                      : "#e1e1e1",
                  color: selectedFile || !isNight ? "white" : "#cfd6e2",
                  fontWeight: "bold",
                  border: "none !important;",
                  transition: "color 0.35s ease, background-color 0.35s ease",
                }}
              >
                <>Upload</>
                <Box width={"10px"}></Box>
                {/* <Check size={"15"}></Check> */}
                <Upload size={"15"}></Upload>
              </Button>
            </ButtonGroup>
          </DialogContent>
        </Grid>
      </Grid>
    </Dialog>
  );
}
